import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router'
import { api, API_BASE, ApiError } from '../lib/api.ts'
import { getToken } from '../lib/auth.ts'
import { friendlyError } from '../lib/friendly-errors.ts'
import { StatusDot, statusLabel } from '../components/StatusDot.tsx'
import { ChatPanel } from '../components/ChatPanel.tsx'
import { IndexingBadgeBar } from '../components/IndexingBadgeBar.tsx'
import { LinksSummary } from '../components/LinksSummary.tsx'

interface DraftResponse {
  id?: string
  slug: string
  status: string
  article: Record<string, unknown> | null
  lintScore: number | null
  lintFindings: LintFinding[]
  qcFindings: QcFinding[]
  modifiedAt: number | null
  modifiedBy: string
  contentHash: string | null
  source: 'd1' | 'github'
  fileSha?: string
  liveUrl?: string | null
}

interface LintFinding {
  rule: string
  category: string
  blocking: boolean
  field: string
  message: string
  raised_at: number
}

interface QcFinding {
  severity: 'high' | 'low'
  issue: string
  quote: string
}

interface StreamEvent {
  id: string
  type: string
  data: unknown
}

/** Loose article-block shape (LLArticleV2 blocks are heterogeneous). */
type B = Record<string, unknown>

/**
 * Editor screen — v0.5 skeleton.
 *
 * v0.5 covers the structural shell: outline (left), form (center), and the
 * save action via PATCH. Live preview iframe lands in step 11 once the
 * /api/preview route ships in chc-revamp (step 10). Per-slice regen + diff
 * land in step 16 (SSE infra in step 14).
 *
 * For v0.5 we expose every flat-keyed string field as a textarea — the
 * cockpit will render typed widgets per slot in step 11.
 */
interface PreviewUrl {
  url: string
  draftId: string
  expiresAt: number
  refreshAfter: number
}

export function ArticleEditor() {
  const { siteId = 'chc', slug = '' } = useParams()
  const [data, setData] = useState<DraftResponse | null>(null)
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)
  const [preview, setPreview] = useState<PreviewUrl | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [events, setEvents] = useState<StreamEvent[]>([])
  const [chatOpen, setChatOpen] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishMsg, setPublishMsg] = useState<string | null>(null)
  const [blockingFindings, setBlockingFindings] = useState<LintFinding[]>([])
  // LLArticleV2 structured edits (blocks + hero illustration). Kept apart
  // from the flat `edits` map; merged into the article at save time.
  const [blocksEdit, setBlocksEdit] = useState<B[] | null>(null)
  const [heroEdit, setHeroEdit] = useState<B | null>(null)
  const [redrawBusy, setRedrawBusy] = useState<string | null>(null)
  const [proposing, setProposing] = useState<string | null>(null)
  const [applying, setApplying] = useState<string | null>(null)
  const [proposals, setProposals] = useState<Record<string, { before: string; options: string[] }>>({})
  const esRef = useRef<EventSource | null>(null)
  const autoSaveFails = useRef(0)

  useEffect(() => {
    setLoading(true)
    setError(null)
    api<DraftResponse>(`/api/sites/${siteId}/drafts/${slug}`)
      .then((res) => {
        setData(res)
        setEdits({})
        setBlocksEdit(null)
        setHeroEdit(null)
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false))
  }, [siteId, slug])

  // Live generation feedback: while the oneshot engine is writing this
  // draft (stub carries GENERATION/STAGE fields), refetch every 15s so
  // the operator watches progress without manual refreshes.
  const generating = useMemo(() => {
    const aj = data?.article as Record<string, unknown> | null | undefined
    if (!aj || Array.isArray((aj as { blocks?: unknown[] }).blocks)) return null
    if (typeof aj.GENERATION !== 'string') return null
    return {
      state: aj.GENERATION as string,
      stage: (aj.STAGE as string) ?? '',
      error: (aj.ERROR as string) ?? '',
      fix: (aj.FIX as string) ?? '',
    }
  }, [data])
  useEffect(() => {
    if (!generating || generating.state === 'FAILED') return
    const t = window.setInterval(() => {
      api<DraftResponse>(`/api/sites/${siteId}/drafts/${slug}`)
        .then((res) => setData(res))
        .catch(() => {})
    }, 15_000)
    return () => window.clearInterval(t)
  }, [generating, siteId, slug])

  // SSE: subscribe to the draft's event stream once we have a draft id.
  useEffect(() => {
    if (!data?.id) return
    const token = getToken()
    // EventSource doesn't support custom headers; pass token in query.
    const url = `${API_BASE}/api/drafts/${data.id}/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`
    const es = new EventSource(url, { withCredentials: false })
    esRef.current = es
    const handler = (ev: MessageEvent) => {
      setEvents((prev) => [...prev.slice(-50), { id: ev.lastEventId, type: ev.type, data: parse(ev.data) }])
    }
    // Subscribe to known event types — EventSource fires onmessage only for
    // 'message' events; named events need addEventListener.
    for (const t of ['hello', 'slice.start', 'slice.complete', 'slice.error', 'publish.staged', 'publish.deployed', 'publish.indexed', 'cycle']) {
      es.addEventListener(t, handler)
    }
    es.onerror = () => {
      // EventSource auto-reconnects; we just log
      console.warn('SSE error, will reconnect')
    }
    return () => {
      es.close()
      esRef.current = null
    }
  }, [data?.id])

  // Fetch the signed preview URL once we know it's a draft (not a github read).
  useEffect(() => {
    if (!data || data.source !== 'd1') {
      setPreview(null)
      return
    }
    let cancelled = false
    api<PreviewUrl>(`/api/sites/${siteId}/drafts/${slug}/preview-url`)
      .then((res) => {
        if (!cancelled) setPreview(res)
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(friendlyError(err).message)
      })
    return () => {
      cancelled = true
    }
  }, [siteId, slug, data?.source, data?.modifiedAt])

  const editableFields = useMemo(() => {
    if (!data?.article) return []
    return Object.entries(data.article)
      .filter(([k, v]) => typeof v === 'string' && shouldShowField(k, v as string))
      .map(([k, v]) => ({ key: k, value: String(v) }))
      .sort((a, b) => fieldSortKey(a.key).localeCompare(fieldSortKey(b.key)))
  }, [data])

  function setField(key: string, value: string) {
    setEdits((prev) => ({ ...prev, [key]: value }))
  }

  async function openAsDraft() {
    try {
      const res = await api<{ id: string; slug: string }>(
        `/api/sites/${siteId}/drafts`,
        { method: 'POST', body: JSON.stringify({ slug }) },
      )
      // Re-fetch the draft (now sourced from d1).
      const fresh = await api<DraftResponse>(`/api/sites/${siteId}/drafts/${res.slug}`)
      setData(fresh)
      setEdits({})
    } catch (err) {
      alert(`Open as draft failed: ${(err as Error).message}`)
    }
  }

  // Per-section regen was removed — it ran the full factory + overwrote
  // the article, which wasn't what 'regen' implied. Use the Chat panel
  // (suggestions + copy/paste) for targeted edits instead.

  async function publish(force = false) {
    setPublishing(true)
    setPublishMsg(null)
    setBlockingFindings([])
    try {
      if (!force) {
        const dupRes = await api<{ isDuplicate: boolean; lastPublishAt?: number }>(
          `/api/sites/${siteId}/drafts/${slug}/publish/check-duplicate`,
        )
        if (dupRes.isDuplicate) {
          const ts = dupRes.lastPublishAt ? new Date(dupRes.lastPublishAt * 1000).toLocaleString() : 'recently'
          if (!confirm(`Duplicate publish: same content as the publish at ${ts}. Publish anyway?`)) {
            setPublishing(false)
            return
          }
          force = true
        }
      }
      const res = await api<{ ok: boolean; commitSha?: string; status?: string }>(
        `/api/sites/${siteId}/drafts/${slug}/publish`,
        { method: 'POST', body: JSON.stringify({ force }) },
      )
      setPublishMsg(`Published. commit ${res.commitSha?.slice(0, 7)} → status ${res.status}.`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { error?: string; findings?: LintFinding[]; blockingCount?: number }
        if (body.error === 'lint_blocking' && body.findings) {
          setBlockingFindings(body.findings.filter((f) => f.blocking))
          setPublishMsg(`Lint blocked publish (${body.blockingCount} blocking finding(s)). Fix + retry.`)
        } else {
          setPublishMsg(`Publish failed: ${err.message}`)
        }
      } else if (err instanceof ApiError && err.status === 409) {
        setPublishMsg('Duplicate publish blocked. Retry with force=true.')
      } else {
        setPublishMsg(`Publish failed: ${(err as Error).message}`)
      }
    } finally {
      setPublishing(false)
    }
  }

  async function save() {
    if (!data) return
    if (data.source === 'github') {
      setError('Open this article as a draft first (use the grid).')
      return
    }
    setSaving(true)
    setSaveMsg(null)
    try {
      const merged = {
        ...(data.article ?? {}),
        ...edits,
        ...(blocksEdit ? { blocks: blocksEdit } : {}),
        ...(heroEdit ? { heroIllustration: heroEdit } : {}),
      }
      const res = await api<{ ok: boolean; lintScore: number | null; contentHash?: string }>(
        `/api/sites/${siteId}/drafts/${slug}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ article: merged, baseHash: data.contentHash ?? undefined }),
        },
      )
      const liveAlready = ['staged', 'deployed', 'indexed'].includes(data.status)
      setSaveMsg(
        `Saved. Lint score: ${res.lintScore ?? '—'}.` +
          (liveAlready ? ' Heads up: the live site keeps the old version until you Publish again.' : ''),
      )
      // Bump modifiedAt locally so the preview effect signs a fresh URL and
      // the iframe reloads with what was just saved.
      setData({
        ...data,
        article: merged,
        lintScore: res.lintScore,
        contentHash: res.contentHash ?? data.contentHash,
        modifiedAt: Math.floor(Date.now() / 1000),
      })
      setEdits({})
      setBlocksEdit(null)
      setHeroEdit(null)
      autoSaveFails.current = 0
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        autoSaveFails.current = 2 // stand down; the user must refresh first
        setSaveMsg(
          'Not saved: this article changed somewhere else (another tab or device). Refresh the page to load the newest version.',
        )
        return
      }
      autoSaveFails.current += 1
      setSaveMsg(
        `Save failed: ${(err as Error).message}` +
          (autoSaveFails.current >= 2 ? ' Auto-save paused; use the Save button.' : ''),
      )
    } finally {
      setSaving(false)
    }
  }

  async function rewriteText(
    target: { blockIndex: number; childIndex?: number },
    instruction: string,
  ) {
    const key = `t.${target.blockIndex}.${target.childIndex ?? 'top'}`
    setRedrawBusy(key)
    try {
      const res = await api<{ text: string }>(
        `/api/sites/${siteId}/drafts/${slug}/rewrite-text`,
        { method: 'POST', body: JSON.stringify({ ...target, instruction }) },
      )
      const cur = [...(blocksEdit ?? (((data?.article as B | null)?.blocks as B[]) ?? []))]
      if (target.childIndex === undefined) {
        cur[target.blockIndex] = { ...(cur[target.blockIndex] as B), text: res.text }
      } else {
        const b = { ...(cur[target.blockIndex] as B & { children?: B[] }) }
        const children = [...(b.children ?? [])]
        children[target.childIndex] = { ...(children[target.childIndex] ?? {}), text: res.text }
        b.children = children
        cur[target.blockIndex] = b
      }
      setBlocksEdit(cur)
    } catch (err) {
      alert(`Rewrite failed: ${(err as Error).message}`)
    } finally {
      setRedrawBusy(null)
    }
  }

  async function rebuildFromSource() {
    if (
      !confirm(
        'Rebuild this article cleanly from its generated source? Any manual text edits you saved will be replaced.',
      )
    )
      return
    try {
      await api(`/api/sites/${siteId}/drafts/${slug}/reconvert`, { method: 'POST' })
      const fresh = await api<DraftResponse>(`/api/sites/${siteId}/drafts/${slug}`)
      setData(fresh)
      setEdits({})
      setBlocksEdit(null)
      setHeroEdit(null)
      setSaveMsg('Rebuilt from source.')
    } catch (err) {
      alert(`Rebuild failed: ${friendlyError(err as Error).message}`)
    }
  }

  // Ask DeepSeek for fix options for one finding (no change is made yet).
  async function proposeFix(f: QcFinding) {
    setProposing(f.quote)
    try {
      const res = await api<{ before: string; options: string[] }>(
        `/api/sites/${siteId}/drafts/${slug}/qc-fix`,
        { method: 'POST', body: JSON.stringify({ quote: f.quote, issue: f.issue }) },
      )
      setProposals((p) => ({ ...p, [f.quote]: res }))
    } catch (err) {
      alert(`Could not get fix options: ${friendlyError(err as Error).message}`)
    } finally {
      setProposing(null)
    }
  }

  // Apply the option the operator picked, then refresh.
  async function applyFix(f: QcFinding, text: string) {
    setApplying(f.quote)
    try {
      await api(`/api/sites/${siteId}/drafts/${slug}/qc-fix/apply`, {
        method: 'POST',
        body: JSON.stringify({ quote: f.quote, text }),
      })
      setProposals((p) => {
        const next = { ...p }
        delete next[f.quote]
        return next
      })
      const fresh = await api<DraftResponse>(`/api/sites/${siteId}/drafts/${slug}`)
      setData(fresh)
      setBlocksEdit(null)
      setHeroEdit(null)
      setSaveMsg('Applied your chosen fix.')
    } catch (err) {
      alert(`Apply failed: ${friendlyError(err as Error).message}`)
    } finally {
      setApplying(null)
    }
  }

  function dismissFix(quote: string) {
    setProposals((p) => {
      const next = { ...p }
      delete next[quote]
      return next
    })
  }

  async function archiveDraft() {
    if (!confirm('Archive this article? It disappears from the list (nothing is deleted forever).'))
      return
    try {
      await api(`/api/sites/${siteId}/drafts/${slug}/archive`, { method: 'POST' })
      window.location.href = `/sites/${siteId}/articles`
    } catch (err) {
      alert(`Archive failed: ${friendlyError(err as Error).message}`)
    }
  }

  async function unpublish() {
    if (
      !confirm(
        'Take this article off the live site? The page is removed on the next site build (~5 min). The draft stays here for editing and re-publishing.',
      )
    )
      return
    try {
      await api(`/api/sites/${siteId}/drafts/${slug}/unpublish`, { method: 'POST' })
      setPublishMsg('Unpublished. The live page disappears within ~5 minutes.')
      const fresh = await api<DraftResponse>(`/api/sites/${siteId}/drafts/${slug}`)
      setData(fresh)
    } catch (err) {
      alert(`Unpublish failed: ${friendlyError(err as Error).message}`)
    }
  }

  async function resubmitIndexing() {
    setPublishMsg('Resubmitting to Google and Bing…')
    try {
      const res = await api<{
        url: string
        results: {
          indexing_api_ok: number
          indexing_api_error: string | null
          indexnow_ok: number
          sitemap_submit_ok: number
        } | null
      }>(`/api/sites/${siteId}/drafts/${slug}/resubmit-indexing`, { method: 'POST' })
      const r = res.results
      setPublishMsg(
        r
          ? `Google Indexing API: ${r.indexing_api_ok ? 'OK' : `failed (${r.indexing_api_error ?? '?'})`} · Bing/IndexNow: ${r.indexnow_ok ? 'OK' : 'failed'} · Sitemap: ${r.sitemap_submit_ok ? 'OK' : 'failed'} — for ${res.url}`
          : 'Submitted; no result row.',
      )
    } catch (err) {
      setPublishMsg(`Resubmit failed: ${friendlyError(err as Error).message}`)
    }
  }

  async function redrawScene(
    target: { hero?: boolean; blockIndex?: number; childIndex?: number },
    instruction: string,
  ) {
    const key = target.hero ? 'hero' : `${target.blockIndex}.${target.childIndex}`
    setRedrawBusy(key)
    try {
      const res = await api<{ svg: string; alt: string; captionBold: string; captionRest: string }>(
        `/api/sites/${siteId}/drafts/${slug}/redraw-scene`,
        { method: 'POST', body: JSON.stringify({ ...target, instruction }) },
      )
      if (target.hero) {
        const base = (heroEdit ?? ((data?.article as B | null)?.heroIllustration as B)) ?? {}
        setHeroEdit({
          ...base,
          svg: res.svg,
          alt: res.alt,
          captionBold: res.captionBold || base.captionBold,
          captionRest: res.captionRest || base.captionRest,
        })
      } else {
        const cur = [...(blocksEdit ?? (((data?.article as B | null)?.blocks as B[]) ?? []))]
        const b = { ...(cur[target.blockIndex!] as B & { children?: B[] }) }
        const children = [...(b.children ?? [])]
        const ch = { ...(children[target.childIndex!] ?? {}) }
        children[target.childIndex!] = {
          ...ch,
          svg: res.svg,
          alt: res.alt,
          captionBold: res.captionBold || ch.captionBold,
          captionRest: res.captionRest || ch.captionRest,
        }
        b.children = children
        cur[target.blockIndex!] = b
        setBlocksEdit(cur)
      }
    } catch (err) {
      alert(`Redraw failed: ${(err as Error).message}`)
    } finally {
      setRedrawBusy(null)
    }
  }

  // Auto-save 2.5s after the last edit so the live preview refreshes by
  // itself (type, pause a breath, see it). The Save button still works.
  // After 2 consecutive failures auto-save stands down (no silent retry
  // hammering); a manual Save that succeeds re-arms it.
  const dirtyNow = Object.keys(edits).length > 0 || blocksEdit !== null || heroEdit !== null
  useEffect(() => {
    if (!dirtyNow || saving || !data || data.source !== 'd1' || generating) return
    if (autoSaveFails.current >= 2) return
    const t = window.setTimeout(() => {
      void save()
    }, 2500)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- save is stable enough; deps capture every edit source
  }, [edits, blocksEdit, heroEdit, dirtyNow, saving, generating, data?.source])

  if (loading) return <div className="loading">Loading article…</div>
  if (error) return <div className="error-state">{error}</div>
  if (!data) return <div className="empty">Not found.</div>

  const dirty = dirtyNow
  const articleObj = data.article as B | null
  const rawBlocks = articleObj?.blocks
  const llBlocks = blocksEdit ?? (Array.isArray(rawBlocks) ? (rawBlocks as B[]) : null)
  const llHero =
    heroEdit ??
    (articleObj?.heroIllustration && typeof articleObj.heroIllustration === 'object'
      ? (articleObj.heroIllustration as B)
      : null)

  return (
    <>
      {generating ? (
        <div
          style={{
            margin: '8px 0',
            padding: '10px 14px',
            borderRadius: 8,
            background: generating.state === 'FAILED' ? '#fdecea' : '#e8f4f1',
            color: generating.state === 'FAILED' ? '#b3261e' : '#0f5132',
            fontSize: 14,
          }}
        >
          {generating.state === 'FAILED' ? (
            <>
              <strong>Generation failed:</strong> {generating.error}
              {generating.fix ? <div>{generating.fix}</div> : null}
            </>
          ) : (
            <>
              <strong>Writing this article…</strong>{' '}
              {generating.stage || 'starting'} — updates itself every 15s, no
              refresh needed.
            </>
          )}
        </div>
      ) : null}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: '1.25rem' }}>
            <Link to={`/sites/${siteId}/articles`} style={{ color: 'var(--text-faint)' }}>
              Articles
            </Link>
            <span style={{ color: 'var(--text-faint)' }}> / </span>
            <span title={slug}>{getTitle(data.article)}</span>
          </h1>
          <div className="subtitle">
            <StatusDot status={data.status} />
            <span title={statusLabel(data.status).hint}>{statusLabel(data.status).label}</span>
            {data.lintScore !== null && <> · quality score {data.lintScore}/100</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {data.source === 'github' ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={openAsDraft}
              title="Clone into D1 so you can edit + preview + publish"
            >
              Open as draft
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn"
                onClick={() => setChatOpen(true)}
                title="Chat with DeepSeek about this article"
              >
                💬 Chat with AI
              </button>
              <button
                type="button"
                className="btn"
                disabled={!dirty || saving}
                onClick={save}
              >
                {saving ? 'Saving…' : dirty ? 'Save edits' : 'Saved'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={publishing || dirty}
                onClick={() => publish(false)}
                title={dirty ? 'Save edits before publishing' : 'Publish to GitHub (will trigger CF Pages build)'}
              >
                {publishing ? 'Publishing…' : 'Publish'}
              </button>
            </>
          )}
        </div>
      </div>

      <IndexingBadgeBar siteId={siteId} slug={slug} />

      {data.source === 'd1' && Array.isArray(data.qcFindings) ? (
        <div
          className="card"
          style={{
            margin: '0.5rem 0',
            gap: '0.4rem',
            borderColor: data.qcFindings.length ? 'var(--warn)' : 'var(--border)',
          }}
        >
          <strong style={{ fontSize: '0.85rem' }}>
            DeepSeek QC{' '}
            {data.qcFindings.length
              ? `· ${data.qcFindings.length} issue${data.qcFindings.length > 1 ? 's' : ''} to review`
              : '· clean ✓'}
          </strong>
          {data.qcFindings.map((f, i) => {
            const prop = proposals[f.quote]
            return (
              <div
                key={i}
                style={{
                  fontSize: '0.8rem',
                  color: 'var(--text-muted)',
                  borderTop: i ? '1px solid var(--border)' : undefined,
                  paddingTop: i ? '0.5rem' : undefined,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem' }}>
                  <span>
                    <span style={{ color: f.severity === 'high' ? 'var(--danger)' : 'var(--warn)', fontWeight: 600 }}>
                      {f.issue}
                    </span>{' '}
                    — <span style={{ fontStyle: 'italic' }}>“{f.quote}”</span>
                  </span>
                  {!prop && (
                    <button
                      type="button"
                      className="btn"
                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem', whiteSpace: 'nowrap' }}
                      disabled={proposing === f.quote}
                      onClick={() => proposeFix(f)}
                    >
                      {proposing === f.quote ? 'Thinking…' : 'Fix with DeepSeek'}
                    </button>
                  )}
                </div>
                {prop && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
                      Now: “{prop.before.slice(0, 220)}{prop.before.length > 220 ? '…' : ''}”
                    </div>
                    {prop.options.map((opt, oi) => (
                      <div
                        key={oi}
                        style={{
                          display: 'flex',
                          gap: '0.5rem',
                          alignItems: 'flex-start',
                          background: 'var(--bg-muted)',
                          borderRadius: 'var(--radius)',
                          padding: '0.5rem',
                        }}
                      >
                        <span style={{ flex: 1, color: 'var(--text)' }}>{opt}</span>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem', whiteSpace: 'nowrap' }}
                          disabled={applying === f.quote}
                          onClick={() => applyFix(f, opt)}
                        >
                          {applying === f.quote ? 'Applying…' : 'Use this'}
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn"
                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem', alignSelf: 'flex-start' }}
                      disabled={applying === f.quote}
                      onClick={() => dismissFix(f.quote)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: '0.5rem', margin: '0.25rem 0 0.75rem', flexWrap: 'wrap' }}>
        {data.liveUrl && ['staged', 'deployed', 'indexed', 'published'].includes(data.status) ? (
          <a
            className="btn"
            style={{ fontSize: '0.8rem' }}
            href={data.liveUrl}
            target="_blank"
            rel="noreferrer"
          >
            View live ↗
          </a>
        ) : null}
        {data.source === 'd1' ? (
          <button type="button" className="btn" style={{ fontSize: '0.8rem' }} onClick={rebuildFromSource}>
            Rebuild from source
          </button>
        ) : null}
        {['staged', 'deployed', 'indexed', 'published'].includes(data.status) ? (
          <button type="button" className="btn" style={{ fontSize: '0.8rem' }} onClick={unpublish}>
            Unpublish
          </button>
        ) : null}
        {['staged', 'deployed', 'indexed'].includes(data.status) ? (
          <button
            type="button"
            className="btn"
            style={{ fontSize: '0.8rem' }}
            onClick={resubmitIndexing}
            title="Re-send this article's URL to Google's Indexing API, Bing's IndexNow, and the sitemap"
          >
            Resubmit to Google
          </button>
        ) : null}
        {data.source === 'd1' ? (
          <button type="button" className="btn" style={{ fontSize: '0.8rem' }} onClick={archiveDraft}>
            Archive
          </button>
        ) : null}
      </div>

      {saveMsg && <div className="loading" style={{ marginBottom: '1rem' }}>{saveMsg}</div>}
      {publishMsg && (
        <div
          className={blockingFindings.length > 0 ? 'error-state' : 'loading'}
          style={{ marginBottom: '1rem' }}
        >
          {publishMsg}
          {blockingFindings.length > 0 && (
            <ul style={{ marginTop: '0.5rem', textAlign: 'left', listStyle: 'none', padding: 0 }}>
              {blockingFindings.slice(0, 8).map((f, i) => (
                <li key={i} style={{ marginBottom: '0.35rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById(f.field)
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        ;(el as HTMLInputElement).focus()
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--danger)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    <strong>{f.field}</strong> — {f.message}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {events.length > 0 && (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            background: 'var(--bg-muted)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '0.5rem 0.75rem',
            marginBottom: '1rem',
          }}
        >
          <strong>Live events: </strong>
          {events
            .slice(-3)
            .map((e) => e.type + (e.type.startsWith('slice') ? `(${(e.data as { sliceId?: string })?.sliceId ?? ''})` : ''))
            .join(' → ')}
        </div>
      )}

      <div className="editor">
        <nav className="editor-outline">
          {sectionGroups(editableFields).map((group) => (
            <a key={group.key} href={`#group-${group.key}`} className="outline-item">
              {group.label}
            </a>
          ))}
          <LinksSummary siteId={siteId} slug={slug} />
        </nav>

        <div className="editor-form">
          {llBlocks ? (
            <LLSections
              blocks={llBlocks}
              hero={llHero}
              busy={redrawBusy}
              onBlocks={setBlocksEdit}
              onHero={setHeroEdit}
              onRedraw={redrawScene}
              onRewrite={rewriteText}
            />
          ) : null}
          {llBlocks ? (
            <VersionsPanel
              siteId={siteId}
              slug={slug}
              onSwitched={() => {
                api<DraftResponse>(`/api/sites/${siteId}/drafts/${slug}`)
                  .then((res) => {
                    setData(res)
                    setEdits({})
                    setBlocksEdit(null)
                    setHeroEdit(null)
                  })
                  .catch(() => {})
              }}
            />
          ) : null}
          {sectionGroups(editableFields).map((group) => (
            <div key={group.key} id={`group-${group.key}`} style={{ marginBottom: '2rem' }}>
              <h2
                style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--text-faint)',
                  marginBottom: '0.75rem',
                  borderBottom: '1px solid var(--border)',
                  paddingBottom: '0.4rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{group.label}</span>
              </h2>
              {group.fields.map((f) => {
                const isChart = f.key.endsWith('.svg')
                const chartLabel = isChart ? chartSummary(f.value) : null
                return (
                  <div key={f.key} className="field">
                    <label htmlFor={f.key}>
                      {isChart && chartLabel ? `${f.key} · ${chartLabel}` : f.key}
                    </label>
                    {isChart || f.value.length > 80 ? (
                      <textarea
                        id={f.key}
                        value={edits[f.key] ?? f.value}
                        onChange={(e) => setField(f.key, e.target.value)}
                        style={isChart ? { fontSize: '0.75rem', minHeight: '4rem' } : undefined}
                      />
                    ) : (
                      <input
                        id={f.key}
                        type="text"
                        value={edits[f.key] ?? f.value}
                        onChange={(e) => setField(f.key, e.target.value)}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        <aside className="editor-preview">
          <header>
            <span>Live preview</span>
            <span style={{ color: 'var(--text-faint)' }}>
              {data.source === 'github'
                ? 'open as draft to preview'
                : preview
                  ? `signed until ${new Date(preview.expiresAt * 1000).toLocaleTimeString()}`
                  : previewError
                    ? 'preview unavailable'
                    : 'fetching link…'}
            </span>
          </header>
          {previewError ? (
            <div className="preview-empty">{previewError}</div>
          ) : preview ? (
            <iframe
              title="ContentHub preview"
              src={preview.url}
              // No sandbox: chc-revamp's /preview/ is a page we built. Chrome
              // refuses to render cross-origin iframes when the sandbox combo
              // `allow-scripts allow-same-origin` is used (creates a same-
              // origin escape hatch). Dropping the attribute entirely lets
              // normal cross-origin policy apply.
              referrerPolicy="no-referrer"
            />
          ) : data.source === 'github' ? (
            <div className="preview-empty">
              This article is read-only. Click "Open as draft" from the grid to
              edit + see a live preview.
            </div>
          ) : (
            <div className="preview-empty">Generating signed preview URL…</div>
          )}
        </aside>
      </div>
      <ChatPanel siteId={siteId} slug={slug} open={chatOpen} onClose={() => setChatOpen(false)} />
    </>
  )
}

function getTitle(article: Record<string, unknown> | null): string {
  if (!article) return ''
  return (article['hero.h1'] as string) || (article['h1'] as string) || (article['keyword'] as string) || ''
}

/**
 * Group fields by their prefix (hero, s1, s2 ... close).
 * Plain top-level fields go into 'meta'.
 */
function sectionGroups(fields: { key: string; value: string }[]): {
  key: string
  label: string
  regenSliceId?: string
  fields: { key: string; value: string }[]
}[] {
  const groups = new Map<string, { key: string; value: string }[]>()
  for (const f of fields) {
    const prefix = f.key.includes('.') ? f.key.slice(0, f.key.indexOf('.')) : 'meta'
    if (!groups.has(prefix)) groups.set(prefix, [])
    groups.get(prefix)!.push(f)
  }
  // Sort groups in a stable, intuitive order.
  const order = ['meta', 'hero', 'realtalk', 's1', 's2', 's3', 's4', 's5', 's6', 'faq', 'close']
  // Maps from group prefix → n8n slice id (must match slices.js).
  const sliceMap: Record<string, string> = {
    hero: 'hero',
    realtalk: 'realtalk',
    s1: 'section1', s2: 'section2', s3: 'section3',
    s4: 'section4', s5: 'section5', s6: 'section6',
    faq: 'faq',
    close: 'close',
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (order.indexOf(a) - order.indexOf(b)) || a.localeCompare(b))
    .map(([key, fs]) => ({
      key,
      label: key === 'meta' ? 'metadata' : key,
      regenSliceId: sliceMap[key],
      fields: fs.sort((a, b) => fieldSortKey(a.key).localeCompare(fieldSortKey(b.key))),
    }))
}

function parse(s: string): unknown {
  try {
    return JSON.parse(s)
  } catch {
    return s
  }
}

/**
 * Decide whether a flat-keyed field is worth showing in the editor.
 *
 * The CHC factory leaves a lot of empty-string scaffolding in articles
 * (e.g. `realtalk_svg.title=""`, `s1.realtalk.headline=""`, top-level
 * `hero=""`). They serve no rendering purpose and clutter the form.
 *
 * Rules:
 *   - hide empty strings
 *   - hide pure factory metadata (row_number, status, preview_url, etc.)
 *   - hide the underscore-style legacy keys (realtalk_svg.*, realtalk_*)
 *   - hide stale sN.realtalk.* fields (factory cruft, never populated)
 *   - hide bare prefix keys (hero="", s1="", realtalk="")
 *   - hide notes (factory lint report, not editable copy)
 */
function shouldShowField(key: string, value: string): boolean {
  if (!value) return false
  // Factory metadata — not editable copy.
  if (/^(row_number|status|preview_url|live_url|generatedAt|notes|svg)$/.test(key)) return false
  // Legacy underscore-style keys produced by older factory iterations.
  if (/^realtalk_/.test(key)) return false
  // Stale per-section realtalk scaffolding.
  if (/^s\d+\.realtalk\./.test(key)) return false
  // Bare prefix keys (the "hero": "" container that wraps hero.*).
  if (/^(hero|realtalk|s[1-6]|takeaway[1-4])$/.test(key)) return false
  return true
}

/** Show "kind=bar, title=X..." next to chart field labels. */
function chartSummary(raw: string): string {
  try {
    const obj = JSON.parse(raw) as { kind?: string; title?: string }
    const kind = obj.kind ?? '?'
    const title = obj.title ? obj.title.slice(0, 50) + (obj.title.length > 50 ? '…' : '') : ''
    return `kind=${kind}${title ? ` · "${title}"` : ''}`
  } catch {
    return ''
  }
}

// ---------------------------------------------------------------------------
// LLArticleV2 sections editor — every paragraph, heading, quote, table cell,
// FAQ entry, and illustration as a plain editable control, in article order.
// Fixed elements (CTAs, table of contents, related links) show as chips.
// ---------------------------------------------------------------------------

const BLOCK_LABEL: Record<string, string> = {
  sceneHook: 'Opening scene',
  note: 'Author note',
  answerCapsule: 'The short answer',
}

function LLSections(props: {
  blocks: B[]
  hero: B | null
  busy: string | null
  onBlocks: (next: B[]) => void
  onHero: (next: B) => void
  onRedraw: (
    target: { hero?: boolean; blockIndex?: number; childIndex?: number },
    instruction: string,
  ) => void
  onRewrite: (target: { blockIndex: number; childIndex?: number }, instruction: string) => void
}) {
  const { blocks, hero, busy, onBlocks, onHero, onRedraw, onRewrite } = props
  const rewriteRow = (target: { blockIndex: number; childIndex?: number }) => (
    <RewriteRow
      busy={busy === `t.${target.blockIndex}.${target.childIndex ?? 'top'}`}
      onGo={(instruction) => onRewrite(target, instruction)}
    />
  )
  const setBlock = (bi: number, next: B) => {
    const copy = [...blocks]
    copy[bi] = next
    onBlocks(copy)
  }
  const setChild = (bi: number, ci: number, next: B) => {
    const b = blocks[bi] as B & { children?: B[] }
    const children = [...(b.children ?? [])]
    children[ci] = next
    setBlock(bi, { ...b, children })
  }

  const groupHeading = (label: string) => (
    <h2
      style={{
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--text-faint)',
        margin: '1.25rem 0 0.75rem',
        borderBottom: '1px solid var(--border)',
        paddingBottom: '0.4rem',
      }}
    >
      {label}
    </h2>
  )
  const chip = (label: string) => (
    <div
      style={{
        fontSize: 12,
        color: 'var(--text-faint)',
        border: '1px dashed var(--border)',
        borderRadius: 6,
        padding: '6px 10px',
        margin: '10px 0',
      }}
    >
      {label}
    </div>
  )
  const textArea = (label: string, value: string, onChange: (v: string) => void) => (
    <div className="field">
      <label>{label}</label>
      <textarea
        value={value}
        rows={Math.max(2, Math.min(8, Math.ceil(value.length / 90)))}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
  const textInput = (label: string, value: string, onChange: (v: string) => void) => (
    <div className="field">
      <label>{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  )

  return (
    <div id="group-sections" style={{ marginBottom: '2rem' }}>
      {groupHeading('Article sections')}
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
        Edit any text below, then click <strong>Save edits</strong> — the live
        preview refreshes after saving. For drawings, type what you want and hit
        Redraw.
      </div>
      {hero ? (
        <SvgCard
          label="Hero illustration (top of the article)"
          node={hero}
          busy={busy === 'hero'}
          onChange={onHero}
          onRedraw={(instruction) => onRedraw({ hero: true }, instruction)}
        />
      ) : null}
      {blocks.map((b, bi) => {
        const type = String(b.type ?? '')
        if (type === 'sceneHook' || type === 'note' || type === 'answerCapsule') {
          return (
            <div key={bi}>
              {textArea(BLOCK_LABEL[type], String(b.text ?? ''), (v) =>
                setBlock(bi, { ...b, text: v }),
              )}
              {rewriteRow({ blockIndex: bi })}
            </div>
          )
        }
        if (type === 'toc') return <div key={bi}>{chip('Table of contents (built automatically)')}</div>
        if (type === 'ctaQuiz') return <div key={bi}>{chip('Quiz call-to-action (fixed)')}</div>
        if (type === 'ctaScan') return <div key={bi}>{chip('Resume-scan call-to-action (fixed)')}</div>
        if (type === 'internalLinks') return <div key={bi}>{chip('Related articles (built automatically)')}</div>
        if (type === 'tableV2') {
          const headers = (b.headers as string[]) ?? []
          const rows = (b.rows as string[][]) ?? []
          return (
            <div key={bi}>
              {groupHeading('Comparison table')}
              <div style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 6 }}>
                Separate columns with a vertical bar |
              </div>
              {textInput('Header row', headers.join(' | '), (v) =>
                setBlock(bi, { ...b, headers: v.split('|').map((x) => x.trim()) }),
              )}
              {rows.map((row, ri) => (
                <div key={ri}>
                  {textInput(`Row ${ri + 1}`, row.join(' | '), (v) =>
                    setBlock(bi, {
                      ...b,
                      rows: rows.map((r, j) => (j === ri ? v.split('|').map((x) => x.trim()) : r)),
                    }),
                  )}
                </div>
              ))}
            </div>
          )
        }
        if (type === 'faqV2') {
          const items = (b.items as { q: string; a: string }[]) ?? []
          return (
            <div key={bi}>
              {groupHeading('Reader questions (FAQ)')}
              {items.map((it, ii) => (
                <div key={ii} style={{ marginBottom: 10 }}>
                  {textInput(`Question ${ii + 1}`, it.q, (v) =>
                    setBlock(bi, {
                      ...b,
                      items: items.map((x, j) => (j === ii ? { ...x, q: v } : x)),
                    }),
                  )}
                  {textArea('Answer', it.a, (v) =>
                    setBlock(bi, {
                      ...b,
                      items: items.map((x, j) => (j === ii ? { ...x, a: v } : x)),
                    }),
                  )}
                </div>
              ))}
            </div>
          )
        }
        if (type === 'sectionV2') {
          const children = ((b as B & { children?: B[] }).children ?? []) as B[]
          const head = children.find((c) => c.type === 'questionH2')
          return (
            <div key={bi}>
              {groupHeading(String(head?.text ?? 'Section'))}
              {children.map((ch, ci) => {
                const ct = String(ch.type ?? '')
                if (ct === 'questionH2')
                  return (
                    <div key={ci}>
                      {textInput('Section heading', String(ch.text ?? ''), (v) =>
                        setChild(bi, ci, { ...ch, text: v }),
                      )}
                    </div>
                  )
                if (ct === 'h3')
                  return (
                    <div key={ci}>
                      {textInput('Sub-heading', String(ch.text ?? ''), (v) =>
                        setChild(bi, ci, { ...ch, text: v }),
                      )}
                    </div>
                  )
                if (ct === 'pullQuote')
                  return (
                    <div key={ci}>
                      {textArea('Reader quote', String(ch.text ?? ''), (v) =>
                        setChild(bi, ci, { ...ch, text: v }),
                      )}
                      {rewriteRow({ blockIndex: bi, childIndex: ci })}
                    </div>
                  )
                if (ct === 'illustration')
                  return (
                    <SvgCard
                      key={ci}
                      label="Scene illustration"
                      node={ch}
                      busy={busy === `${bi}.${ci}`}
                      onChange={(next) => setChild(bi, ci, next)}
                      onRedraw={(instruction) =>
                        onRedraw({ blockIndex: bi, childIndex: ci }, instruction)
                      }
                    />
                  )
                if (ct === 'p')
                  return (
                    <div key={ci}>
                      {textArea('Paragraph', String(ch.text ?? ''), (v) =>
                        setChild(bi, ci, { ...ch, text: v }),
                      )}
                      {rewriteRow({ blockIndex: bi, childIndex: ci })}
                    </div>
                  )
                return null
              })}
            </div>
          )
        }
        return null
      })}
    </div>
  )
}

/**
 * The judge picked 1 of 3 drafted versions; this panel shows the other two
 * (locked ruling: winner first, others one click away) and can switch the
 * article to one of them — the cron redraws scenes + reconverts (~5 min).
 */
function VersionsPanel(props: { siteId: string; slug: string; onSwitched: () => void }) {
  const [state, setState] = useState<'idle' | 'loading' | 'loaded' | 'none'>('idle')
  const [variants, setVariants] = useState<string[]>([])
  const [winner, setWinner] = useState(1)
  const [switching, setSwitching] = useState(false)

  async function load() {
    if (state === 'loaded' || state === 'loading') return
    setState('loading')
    try {
      const res = await api<{ variants: string[]; winner: number }>(
        `/api/sites/${props.siteId}/drafts/${props.slug}/variants`,
      )
      setVariants(res.variants)
      setWinner(res.winner)
      setState('loaded')
    } catch {
      setState('none')
    }
  }

  async function useVariant(i: number) {
    if (
      !confirm(
        `Switch the article to version ${i + 1}? The writer redraws its scenes and rebuilds the article (~5 min). The current text is replaced.`,
      )
    )
      return
    setSwitching(true)
    try {
      await api(`/api/sites/${props.siteId}/drafts/${props.slug}/use-variant`, {
        method: 'POST',
        body: JSON.stringify({ index: i }),
      })
      props.onSwitched()
    } catch (e) {
      alert(`Switch failed: ${(e as Error).message}`)
    } finally {
      setSwitching(false)
    }
  }

  return (
    <details
      onToggle={(e) => {
        if ((e.target as HTMLDetailsElement).open) void load()
      }}
      style={{ marginBottom: '2rem' }}
    >
      <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        Other versions the writer drafted (the judge picked 1 of 3 — view or switch)
      </summary>
      {state === 'loading' ? <div className="loading">Loading versions…</div> : null}
      {state === 'none' ? (
        <div style={{ fontSize: 13, color: 'var(--text-faint)', padding: '8px 0' }}>
          No stored versions for this article.
        </div>
      ) : null}
      {state === 'loaded'
        ? variants.map((md, i) => (
            <div
              key={i}
              style={{ border: '1px solid var(--border)', borderRadius: 8, margin: '10px 0', padding: 12 }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <strong style={{ fontSize: 14 }}>
                  Version {i + 1}
                  {i + 1 === winner ? ' (the judge picked this one)' : ''}
                </strong>
                <button
                  type="button"
                  className="btn"
                  disabled={switching}
                  onClick={() => void useVariant(i)}
                >
                  {switching ? 'Switching…' : 'Use this version'}
                </button>
              </div>
              <pre
                style={{
                  maxHeight: 260,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontSize: 12,
                  background: 'var(--bg-muted)',
                  padding: 10,
                  borderRadius: 6,
                }}
              >
                {md}
              </pre>
            </div>
          ))
        : null}
    </details>
  )
}

function RewriteRow(props: { busy: boolean; onGo: (instruction: string) => void }) {
  const [prompt, setPrompt] = useState('')
  return (
    <div style={{ display: 'flex', gap: 8, margin: '-6px 0 12px' }}>
      <input
        type="text"
        placeholder="Tell the AI what to change, e.g. shorter and warmer"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        style={{ flex: 1, fontSize: '0.85rem' }}
      />
      <button
        type="button"
        className="btn"
        disabled={props.busy}
        onClick={() => props.onGo(prompt)}
        style={{ fontSize: '0.8rem' }}
      >
        {props.busy ? 'Rewriting…' : 'Rewrite with AI'}
      </button>
    </div>
  )
}

function SvgCard(props: {
  label: string
  node: B
  busy: boolean
  onChange: (next: B) => void
  onRedraw: (instruction: string) => void
}) {
  const [prompt, setPrompt] = useState('')
  const svg = String(props.node.svg ?? '')
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
        margin: '10px 0',
        background: 'var(--bg-muted)',
      }}
    >
      <div
        style={{
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-faint)',
          marginBottom: 8,
        }}
      >
        {props.label}
      </div>
      <div
        style={{ maxWidth: 430, background: '#fff', border: '1px solid var(--border)', borderRadius: 6 }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      <div className="field" style={{ marginTop: 8 }}>
        <label>Caption (bold lead)</label>
        <input
          type="text"
          value={String(props.node.captionBold ?? '')}
          onChange={(e) => props.onChange({ ...props.node, captionBold: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Caption (rest)</label>
        <input
          type="text"
          value={String(props.node.captionRest ?? '')}
          onChange={(e) => props.onChange({ ...props.node, captionRest: e.target.value })}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input
          type="text"
          placeholder="Describe the new drawing, e.g. a payslip with EPF circled"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          style={{ flex: 1 }}
        />
        <button type="button" className="btn" disabled={props.busy} onClick={() => props.onRedraw(prompt)}>
          {props.busy ? 'Drawing… ~15s' : 'Redraw with AI'}
        </button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>
        The new drawing replaces this one on screen only — click Save edits to keep it.
      </div>
    </div>
  )
}

/** Lexical sort but with h1/h2/dek/answer hoisted to the top of each section. */
function fieldSortKey(k: string): string {
  const hoist = ['.h1', '.h2', '.dek', '.badge', '.metaDescription', '.headline', '.title']
  for (const h of hoist) if (k.endsWith(h)) return `0_${k}`
  if (k.endsWith('.body')) return `2_${k}`
  if (k.endsWith('.svg')) return `9_${k}`
  if (k.endsWith('.callout')) return `8_${k}`
  return `5_${k}`
}
